/**
 * Recognition & WoW Crew Member of the Month (2026-06).
 *
 * Three tabs:
 *   Give  — peer-to-peer "High Five" card: pick a teammate, pick the value it
 *           fits, write the story, send. Everyone can give (true peer-to-peer).
 *   Board — the digital Recognition Board: every card grouped under its value.
 *   Spotlight — current WoW Crew Member of the Month + rolling 12-month history.
 *               Managers get an app-assisted "Pick for {month}" flow (the app
 *               ranks last month's recognitions; the manager makes the call).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  ArrowLeft, House, HandHeart, Trophy, Star, PaperPlaneRight,
  Crown, Sparkle, CaretRight, Medal, UserCircle, Camera,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : "");

// ─── tiny avatar with initial fallback ────────────────────────────────
function Avatar({ url, name, size = 40, color = "#2563EB", testid }) {
  const [broken, setBroken] = useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const px = { width: size, height: size };
  if (url && !broken) {
    return (
      <img
        src={abs(url)}
        alt={name}
        onError={() => setBroken(true)}
        className="rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm"
        style={px}
        data-testid={testid}
      />
    );
  }
  return (
    <span
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-black border-2 border-white shadow-sm"
      style={{ ...px, backgroundColor: color, fontSize: size * 0.42 }}
      data-testid={testid}
    >
      {initial}
    </span>
  );
}

export default function RecognitionPage({ user, crew = [], presetRecipientId = "", onBack, onGoHome }) {
  const crewId = user?.crew_id;
  const isManager = !!(user?.is_manager || user?.is_admin || user?.is_district_manager);
  // Personal-first: land on "My High Fives" unless we arrived via "High Five
  // {name}" (preset recipient), in which case jump straight to Give.
  const [tab, setTab] = useState(presetRecipientId ? "give" : "mine");
  const [values, setValues] = useState([]);
  const [personId, setPersonId] = useState("");

  useEffect(() => {
    axios.get(`${API}/recognition/values`).then((r) => setValues(r.data.values || [])).catch(() => {});
  }, []);

  // Deep-link from a notification (lib/notificationRouter stashes
  // `wow:openRecognition`). A store-wide Member-of-the-Month announcement
  // opens the MOTM spotlight so EVERYONE sees the winner — not their own
  // empty board. (User report 2026-10-04: MOTM notif landed everyone on
  // their own board and the winner never got seen.)
  useEffect(() => {
    let payload = null;
    try {
      const raw = sessionStorage.getItem("wow:openRecognition");
      if (raw) { payload = JSON.parse(raw); sessionStorage.removeItem("wow:openRecognition"); }
    } catch { /* ignore */ }
    if (payload && payload.kind === "motm") setTab("spotlight");
  }, []);

  return (
    <div
      className="min-h-screen relative pb-20"
      style={{
        background:
          "radial-gradient(900px 600px at 50% -10%, rgba(37,99,235,0.14), transparent 60%), radial-gradient(800px 600px at 85% 100%, rgba(236,72,153,0.10), transparent 60%), #F8FAFC",
      }}
      data-testid="recognition-page"
    >
      <header
        className="sticky top-0 z-30 backdrop-blur-xl"
        style={{ backgroundColor: "rgba(248,250,252,0.9)", borderBottom: "1px solid rgba(148,163,184,0.18)" }}
      >
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
          <button onClick={onBack} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" aria-label="Back" data-testid="recognition-back">
            <ArrowLeft weight="bold" className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-black text-blue-600 leading-tight">High Fives</p>
            <h1 className="text-base font-black leading-tight text-slate-900 truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Recognition Board
            </h1>
          </div>
          {onGoHome && (
            <button onClick={onGoHome} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="recognition-home">
              <House weight="bold" className="w-5 h-5 text-slate-700" />
            </button>
          )}
        </div>
        {/* tabs */}
        <div className="max-w-2xl mx-auto px-4 pb-2 flex gap-1.5">
          {[
            { k: "mine", label: "Mine", Icon: UserCircle },
            { k: "board", label: "Board", Icon: Star },
            { k: "give", label: "Give", Icon: HandHeart },
            { k: "spotlight", label: "Month", Icon: Trophy },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`recognition-tab-${k}`}
              className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full px-1.5 py-2 text-[11px] font-black transition-all ${
                tab === k ? "bg-slate-900 text-white shadow" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
              }`}
            >
              <Icon weight={tab === k ? "fill" : "duotone"} className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {tab === "mine" && <MyRecognitionTab user={user} onGive={() => setTab("give")} />}
        {tab === "give" && <GiveTab user={user} crew={crew} values={values} presetRecipientId={presetRecipientId} onSent={() => setTab("board")} />}
        {tab === "board" && <BoardTab user={user} onOpenPerson={setPersonId} />}
        {tab === "spotlight" && <SpotlightTab user={user} crew={crew} isManager={isManager} onOpenPerson={setPersonId} />}
      </main>

      {personId && (
        <PersonRecognitionsDialog crewId={personId} actorId={crewId} onClose={() => setPersonId("")} />
      )}
    </div>
  );
}

// ═══════════════════════════ MY RECOGNITION (personal) ═══════════════════════════
const fmtFullDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch (_) { return ""; }
};

function MyRecognitionTab({ user, onGive }) {
  const crewId = user?.crew_id;
  const [rc, setRc] = useState(null);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewCard, setViewCard] = useState(null);

  useEffect(() => {
    if (!crewId) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/recognition/report-card/${crewId}`, { params: { actor_id: crewId } }),
      axios.get(`${API}/recognition/history/${crewId}`, { params: { actor_id: crewId, skip: 0, limit: 20 } }),
    ])
      .then(([r1, r2]) => {
        if (!alive) return;
        setRc(r1.data);
        setHistory(r2.data.cards);
        setTotal(r2.data.total);
        setHasMore(r2.data.has_more);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [crewId]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await axios.get(`${API}/recognition/history/${crewId}`, { params: { actor_id: crewId, skip: history.length, limit: 20 } });
      setHistory((prev) => [...prev, ...r.data.cards]);
      setHasMore(r.data.has_more);
    } catch (_) { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="recognition-mine-loading">
        <div className="h-36 rounded-3xl bg-slate-200/70" />
        <div className="h-40 rounded-3xl bg-slate-200/60" />
      </div>
    );
  }
  if (!rc) return null;
  const maxCount = Math.max(1, ...rc.breakdown.map((b) => b.count));

  return (
    <div className="space-y-5" data-testid="recognition-mine-tab">
      {/* hero */}
      <section
        className="rounded-3xl p-5 text-white shadow-lg relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1d4ed8 0%,#7c3aed 55%,#db2777 100%)" }}
      >
        <div className="flex items-center gap-4">
          <Avatar url={rc.crew?.avatar_url} name={rc.crew?.first_name} size={64} testid="recognition-mine-avatar" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-black uppercase tracking-widest text-white/70">Your High Fives</p>
            <p className="text-5xl font-black leading-none" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }} data-testid="recognition-mine-alltime">{rc.all_time}</p>
            <p className="text-white/80 text-sm font-bold mt-1">{rc.month_total} this month · {rc.given} given</p>
          </div>
        </div>
      </section>

      {/* value breakdown */}
      <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-3">How you're living the values ({rc.month_label})</p>
        <div className="space-y-2">
          {rc.breakdown.map((b) => (
            <div key={b.key} className="flex items-center gap-2" data-testid={`recognition-mine-value-${b.key}`}>
              <span className="text-sm w-5 text-center">{b.emoji}</span>
              <span className="text-[12px] font-bold text-slate-700 w-36 truncate">{b.label}</span>
              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(b.count / maxCount) * 100}%`, backgroundColor: b.color }} />
              </div>
              <span className="text-[12px] font-black text-slate-800 w-5 text-right">{b.count}</span>
            </div>
          ))}
        </div>
      </section>

      {/* history */}
      <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-3">Every High Five you've gotten ({total})</p>
        {total === 0 ? (
          <div className="py-8 text-center" data-testid="recognition-mine-empty">
            <HandHeart weight="duotone" className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 font-bold">No High Fives yet — your moment is coming!</p>
            <p className="text-slate-400 text-sm">Recognize a teammate and spread the love.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((c) => (
              <button
                key={c.id}
                onClick={() => setViewCard(c)}
                className="w-full text-left rounded-2xl border border-slate-200 px-3 py-2.5 hover:border-slate-300 hover:shadow-sm active:scale-[0.99] transition-all"
                data-testid={`recognition-mine-card-${c.id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[12px] font-black text-slate-900">{c.value_emoji} {c.value_label}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtFullDate(c.created_at)}</span>
                </div>
                <p className="text-[12px] text-slate-500">from {c.from_name} · <span className="text-blue-600 font-bold">tap to open</span></p>
                {c.note && <p className="text-[13px] text-slate-700 leading-snug mt-0.5 line-clamp-1">“{c.note}”</p>}
              </button>
            ))}
            {hasMore && (
              <button onClick={loadMore} disabled={loadingMore} className="w-full text-[12px] font-black text-blue-600 hover:text-blue-700 py-2 rounded-lg hover:bg-blue-50" data-testid="recognition-mine-loadmore">
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        )}
      </section>

      <button
        onClick={onGive}
        data-testid="recognition-mine-give"
        className="w-full rounded-full py-3.5 font-black text-white inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg active:scale-[0.98] transition-transform"
      >
        <HandHeart weight="fill" className="w-5 h-5" /> Give someone a High Five
      </button>

      {viewCard && <RecognitionCardViewer card={viewCard} onClose={() => setViewCard(null)} />}
    </div>
  );
}

// ═══════════════════════════ GIVE ═══════════════════════════
function GiveTab({ user, crew = [], values, presetRecipientId = "", onSent }) {
  const crewId = user?.crew_id;
  const [toId, setToId] = useState(presetRecipientId || "");
  const [valueKey, setValueKey] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");

  const teammates = useMemo(
    () =>
      (crew || [])
        .filter((c) => c.id !== crewId && c.active !== false && !c.is_district_manager)
        .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || "")),
    [crew, crewId]
  );
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? teammates.filter((c) => (c.first_name || "").toLowerCase().includes(t)) : teammates;
  }, [teammates, q]);

  const selected = (crew || []).find((c) => c.id === toId);
  const canSend = toId && valueKey && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await axios.post(`${API}/recognition/give`, {
        actor_id: crewId,
        to_id: toId,
        value_key: valueKey,
        note: note.trim(),
      });
      toast.success("High Five sent! 🙌");
      setToId(""); setValueKey(""); setNote(""); setQ("");
      if (onSent) onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="recognition-give-tab">
      {/* recipient */}
      <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2">Recognize a teammate</p>
        {selected ? (
          <div className="flex items-center gap-3">
            <Avatar url={selected.photo_url ? `/api/crew/${selected.id}/avatar` : ""} name={selected.first_name} size={44} testid="recognition-selected-avatar" />
            <div className="flex-1 min-w-0">
              <p className="font-black text-slate-900 truncate">{selected.first_name}</p>
              <p className="text-xs text-slate-500 truncate">{selected.position || "Wow Crew"}</p>
            </div>
            <button onClick={() => setToId("")} className="text-xs font-bold text-blue-600 hover:underline" data-testid="recognition-change-recipient">Change</button>
          </div>
        ) : (
          <>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your crew…"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="recognition-search"
            />
            <div className="max-h-56 overflow-y-auto -mx-1">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setToId(c.id); setQ(""); }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50 active:scale-[0.99] transition"
                  data-testid={`recognition-pick-${c.id}`}
                >
                  <Avatar url={c.photo_url ? `/api/crew/${c.id}/avatar` : ""} name={c.first_name} size={36} />
                  <span className="font-bold text-slate-800 text-sm flex-1 text-left truncate">{c.first_name}</span>
                  <CaretRight weight="bold" className="w-4 h-4 text-slate-300" />
                </button>
              ))}
              {!filtered.length && <p className="text-sm text-slate-400 text-center py-4">No teammates found.</p>}
            </div>
          </>
        )}
      </section>

      {/* value chips */}
      <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-3">Which value did they live?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {values.map((v) => {
            const on = valueKey === v.key;
            return (
              <button
                key={v.key}
                onClick={() => setValueKey(v.key)}
                data-testid={`recognition-value-${v.key}`}
                className={`text-left rounded-2xl px-3 py-2.5 border-2 transition-all active:scale-[0.98] ${on ? "shadow-md" : "border-slate-200 hover:border-slate-300"}`}
                style={on ? { borderColor: v.color, backgroundColor: `${v.color}12` } : {}}
              >
                <span className="font-black text-sm text-slate-900 flex items-center gap-1.5">
                  <span>{v.emoji}</span> {v.label}
                </span>
                <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">{v.blurb}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* note */}
      <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-2">Tell the story <span className="text-slate-300 normal-case font-medium">(optional)</span></p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          rows={3}
          placeholder="What did they do? Make it specific…"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-testid="recognition-note"
        />
        <p className="text-right text-[10px] text-slate-400 mt-1">{note.length}/500</p>
      </section>

      <button
        onClick={send}
        disabled={!canSend}
        data-testid="recognition-send"
        className={`w-full rounded-full py-3.5 font-black text-white inline-flex items-center justify-center gap-2 transition-all ${
          canSend ? "bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg hover:shadow-xl active:scale-[0.98]" : "bg-slate-300 cursor-not-allowed"
        }`}
      >
        <PaperPlaneRight weight="fill" className="w-5 h-5" />
        {sending ? "Sending…" : "Send Recognition"}
      </button>
    </div>
  );
}

// ═══════════════════════════ BOARD ═══════════════════════════
const fmtCardDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (_) { return ""; }
};

function BoardTab({ user, onOpenPerson }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCard, setViewCard] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/recognition/board`, { params: { actor_id: user?.crew_id } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.crew_id]);

  if (loading) return (
    <div className="space-y-4 animate-pulse" data-testid="recognition-board-loading">
      <div className="flex items-center justify-between">
        <div className="h-4 w-28 rounded bg-slate-200" />
        <div className="h-5 w-20 rounded-full bg-slate-200" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="h-11 bg-slate-100" />
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-slate-200" /><div className="flex-1 h-3 rounded bg-slate-200" /></div>
            <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-slate-200" /><div className="flex-1 h-3 rounded bg-slate-200" /></div>
          </div>
        </div>
      ))}
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="recognition-board-tab">
      <div className="flex items-center justify-between px-1">
        <p className="text-sm font-black text-slate-700">{data.month_label}</p>
        <span className="text-[11px] font-black text-white bg-slate-900 rounded-full px-2.5 py-1" data-testid="recognition-board-total">{data.total} High Fives</span>
      </div>
      {data.total === 0 && (
        <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-10 text-center" data-testid="recognition-board-empty">
          <HandHeart weight="duotone" className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 font-bold">No High Fives yet this month.</p>
          <p className="text-slate-400 text-sm">Be the first to recognize a teammate!</p>
        </div>
      )}
      <div className="space-y-4">
        {data.columns.map((col) => (
          <section key={col.key} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`recognition-col-${col.key}`}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: `${col.color}14`, borderBottom: `2px solid ${col.color}` }}>
              <span className="font-black text-sm text-slate-900 flex items-center gap-1.5">{col.emoji} {col.label}</span>
              <span className="text-[11px] font-black text-white rounded-full px-2 py-0.5" style={{ backgroundColor: col.color }}>{col.count}</span>
            </div>
            {col.count === 0 ? (
              <p className="text-xs text-slate-400 px-4 py-3">No cards yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {col.cards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setViewCard(card)}
                    className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                    data-testid={`recognition-card-${card.id}`}
                  >
                    <Avatar url={card.to_avatar} name={card.to_name} size={30} color={col.color} />
                    <p className="flex-1 min-w-0 truncate text-sm text-slate-900">
                      <span className="font-black">{card.to_name}</span>
                      <span className="text-slate-400 font-medium"> · from {card.from_name}</span>
                    </p>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtCardDate(card.created_at)}</span>
                    <CaretRight weight="bold" className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
      {viewCard && <RecognitionCardViewer card={viewCard} own={false} onClose={() => setViewCard(null)} />}
    </div>
  );
}

// ═══════════════════════════ SPOTLIGHT ═══════════════════════════
function SpotlightTab({ user, crew, isManager, onOpenPerson }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickOpen, setPickOpen] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/recognition/motm`, { params: { actor_id: user?.crew_id } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.crew_id]);

  if (loading) return <div className="text-center text-slate-400 py-16" data-testid="recognition-spotlight-loading">Loading…</div>;

  const current = data?.current;
  const past = (data?.winners || []).slice(1);

  return (
    <div className="space-y-5" data-testid="recognition-spotlight-tab">
      {isManager && (
        <button
          onClick={() => setPickOpen(true)}
          data-testid="recognition-pick-open"
          className="w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-white font-black py-3 inline-flex items-center justify-center gap-2 shadow-lg active:scale-[0.98]"
        >
          <Crown weight="fill" className="w-5 h-5" /> Pick Member of the Month
        </button>
      )}

      {current ? (
        <section
          className="rounded-3xl p-6 text-center shadow-xl relative overflow-hidden"
          style={{ background: "linear-gradient(160deg,#1d4ed8 0%,#7c3aed 55%,#db2777 100%)" }}
          data-testid="recognition-motm-current"
        >
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/80 flex items-center justify-center gap-1.5">
            <Sparkle weight="fill" className="w-4 h-4" /> WoW Crew Member of the Month
          </p>
          <p className="text-white/90 font-bold text-sm mt-0.5">{current.month_label}</p>
          <div className="my-4 inline-block relative">
            <div className="absolute -inset-2 rounded-full bg-amber-300/40 blur-md" />
            <button
              onClick={() => onOpenPerson && current.crew_id && onOpenPerson(current.crew_id)}
              className="relative active:scale-95 transition-transform"
              data-testid="recognition-motm-current-open"
            >
              <Avatar url={current.photo_url || current.avatar_url} name={current.name} size={110} color="#f59e0b" testid="recognition-motm-avatar" />
            </button>
          </div>
          <p className="text-2xl font-black text-white" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{current.name}</p>
          <p className="text-white/80 text-sm font-bold">{current.role_label}</p>
          {current.comments && (
            <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl px-4 py-3 text-left">
              <p className="text-[10px] font-black uppercase tracking-wider text-white/70 mb-1">Why they were chosen</p>
              <p className="text-white text-sm leading-snug">“{current.comments}”</p>
            </div>
          )}
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            <span className="text-[11px] font-black text-white bg-white/20 rounded-full px-3 py-1">
              {current.recognition_count} High Fives
            </span>
          </div>
        </section>
      ) : (
        <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-10 text-center" data-testid="recognition-motm-empty">
          <Trophy weight="duotone" className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 font-bold">No Member of the Month picked yet.</p>
          {isManager && <p className="text-slate-400 text-sm">Tap “Pick Member of the Month” to choose.</p>}
        </div>
      )}

      {past.length > 0 && (
        <section className="bg-white rounded-3xl border border-slate-200 p-4 shadow-sm" data-testid="recognition-motm-history">
          <p className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-3">Hall of Fame · last 12 months</p>
          <div className="space-y-2">
            {past.map((w) => (
              <button
                key={w.month_key}
                onClick={() => onOpenPerson && w.crew_id && onOpenPerson(w.crew_id)}
                className="w-full text-left flex items-center gap-3 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors p-1 -m-1"
                data-testid={`recognition-motm-past-${w.month_key}`}
              >
                <Avatar url={w.photo_url || w.avatar_url} name={w.name} size={38} color="#f59e0b" />
                <div className="flex-1 min-w-0">
                  <p className="font-black text-slate-900 text-sm truncate">{w.name}</p>
                  <p className="text-[11px] text-slate-500">{w.month_label}</p>
                </div>
                <Medal weight="fill" className="w-5 h-5 text-amber-400" />
              </button>
            ))}
          </div>
        </section>
      )}

      {pickOpen && (
        <PickMotmDialog user={user} onClose={() => setPickOpen(false)} onPicked={() => { setPickOpen(false); load(); }} />
      )}
    </div>
  );
}

// ─── manager pick flow (app-assisted) ─────────────────────────────────
function PickMotmDialog({ user, onClose, onPicked }) {
  // Last 12 months (current month first) so managers can crown prior months too.
  const months = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      arr.push({ key, label });
    }
    return arr;
  }, []);
  const [month, setMonth] = useState(months[1]?.key || months[0]?.key); // default: last month
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [pq, setPq] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setSel(""); setComments(""); setPhotoFile(null); setPhotoPreview("");
    axios
      .get(`${API}/recognition/motm/candidates`, { params: { actor_id: user?.crew_id, month } })
      .then((r) => { if (!alive) return; setData(r.data); if (r.data.selected) { setSel(r.data.selected.crew_id); setComments(r.data.selected.comments || ""); setPhotoPreview(r.data.selected.photo_url || ""); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, user?.crew_id]);

  const save = async () => {
    if (!sel) { toast.error("Pick a crew member first."); return; }
    setSaving(true);
    try {
      let photo_storage_path = "";
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        form.append("actor_id", user?.crew_id || "");
        form.append("month", month);
        const up = await axios.post(`${API}/recognition/motm/photo`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photo_storage_path = up?.data?.storage_path || "";
      }
      await axios.post(`${API}/recognition/motm`, {
        actor_id: user?.crew_id,
        crew_id: sel,
        month,
        comments: comments.trim(),
        photo_storage_path,
      });
      toast.success("Member of the Month crowned! 🏆");
      onPicked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="p-0 gap-0 max-w-md w-[calc(100%-16px)] max-h-[88vh] overflow-hidden rounded-3xl" data-testid="recognition-pick-dialog">
        <DialogTitle className="sr-only">Pick Member of the Month</DialogTitle>
        <div className="px-5 pt-5 pb-3 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">App-assisted pick</p>
          <p className="text-lg font-black text-slate-900">Member of the Month</p>
          <div className="mt-2 flex items-center gap-2">
            <label className="text-[11px] font-black uppercase tracking-wide text-slate-500 flex-shrink-0">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="recognition-pick-month"
              className="flex-1 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              {months.map((mo) => (
                <option key={mo.key} value={mo.key}>{mo.label}</option>
              ))}
            </select>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">Ranked by that month’s High Fives — you make the final call.</p>
        </div>
        <div className="px-4 pt-3 pb-2">
          <input
            value={pq}
            onChange={(e) => setPq(e.target.value)}
            placeholder="Search crew…"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="recognition-pick-search"
          />
        </div>
        <div className="overflow-y-auto flex-1 max-h-[46vh] px-4 pb-3 space-y-2">
          {loading ? (
            <p className="text-center text-slate-400 py-8">Crunching the numbers…</p>
          ) : !data?.candidates?.length ? (
            <p className="text-center text-slate-400 py-8" data-testid="recognition-pick-none">No crew found for this store.</p>
          ) : (
            data.candidates
              .filter((c) => !pq.trim() || (c.name || "").toLowerCase().includes(pq.trim().toLowerCase()))
              .map((c, i) => {
              const on = sel === c.crew_id;
              return (
                <button
                  key={c.crew_id}
                  onClick={() => setSel(c.crew_id)}
                  data-testid={`recognition-candidate-${c.crew_id}`}
                  className={`w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 border-2 transition-all text-left ${on ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <span className="text-xs font-black text-slate-400 w-5 text-center">{i + 1}</span>
                  <Avatar url={c.avatar_url} name={c.name} size={40} color="#f59e0b" />
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-slate-900 text-sm truncate">{c.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{c.role_label}</p>
                  </div>
                  <span className="text-[11px] font-black text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{c.count}</span>
                </button>
              );
            })
          )}
        </div>
        <div className="px-4 pt-2 pb-4 border-t border-slate-200 space-y-2">
          <div className="flex items-center gap-3">
            {photoPreview ? (
              <img src={photoPreview} alt="Member of the Month" className="w-14 h-14 rounded-full object-cover border-2 border-amber-300 flex-shrink-0" data-testid="recognition-pick-photo-preview" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-amber-50 border-2 border-dashed border-amber-300 flex items-center justify-center text-amber-400 flex-shrink-0">
                <Camera weight="bold" className="w-5 h-5" />
              </div>
            )}
            <label className="flex-1 cursor-pointer rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs font-black text-amber-700 text-center active:scale-[0.98]" data-testid="recognition-pick-photo-btn">
              {photoPreview ? "Change winner photo" : "Add a winner photo (optional)"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="recognition-pick-photo-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); } }}
              />
            </label>
          </div>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value.slice(0, 1000))}
            rows={2}
            placeholder="Why were they chosen? (shown on the spotlight)"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="recognition-pick-comments"
          />
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 rounded-full py-2.5 font-black text-slate-600 bg-slate-100 hover:bg-slate-200" data-testid="recognition-pick-cancel">Cancel</button>
            <button onClick={save} disabled={saving || !sel} className={`flex-1 rounded-full py-2.5 font-black text-white inline-flex items-center justify-center gap-1.5 ${saving || !sel ? "bg-slate-300" : "bg-gradient-to-r from-amber-500 to-orange-500 active:scale-[0.98]"}`} data-testid="recognition-pick-save">
              <Crown weight="fill" className="w-4 h-4" /> {saving ? "Saving…" : "Crown them"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Tap a name on the board / spotlight → see that person's recognitions ──
function PersonRecognitionsDialog({ crewId, actorId, onClose }) {
  const [rc, setRc] = useState(null);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/recognition/report-card/${crewId}`, { params: { actor_id: actorId } }),
      axios.get(`${API}/recognition/history/${crewId}`, { params: { actor_id: actorId, skip: 0, limit: 20 } }),
    ])
      .then(([r1, r2]) => {
        if (!alive) return;
        setRc(r1.data);
        setHistory(r2.data.cards);
        setTotal(r2.data.total);
        setHasMore(r2.data.has_more);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [crewId, actorId]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await axios.get(`${API}/recognition/history/${crewId}`, { params: { actor_id: actorId, skip: history.length, limit: 20 } });
      setHistory((prev) => [...prev, ...r.data.cards]);
      setHasMore(r.data.has_more);
    } catch (_) { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const fmtFull = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch (_) { return ""; }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="p-0 gap-0 max-w-md w-[calc(100%-16px)] max-h-[88vh] overflow-hidden rounded-3xl" data-testid="recognition-person-dialog">
        <DialogTitle className="sr-only">Recognitions</DialogTitle>
        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : (
          <>
            <div className="px-5 pt-5 pb-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-pink-50">
              <div className="flex items-center gap-3">
                <Avatar url={rc?.crew?.avatar_url} name={rc?.crew?.first_name} size={52} />
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-black text-slate-900 truncate" data-testid="recognition-person-name">{rc?.crew?.first_name}</p>
                  <p className="text-[12px] text-slate-500 truncate">{rc?.crew?.role_label}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black leading-none" style={{ color: "#2563EB" }}>{rc?.all_time ?? total}</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">All-time</p>
                </div>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 max-h-[60vh] px-4 py-3 space-y-2">
              {total === 0 ? (
                <p className="text-center text-slate-400 py-8" data-testid="recognition-person-empty">No recognitions yet.</p>
              ) : (
                history.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-slate-200 px-3 py-2.5" data-testid={`recognition-person-card-${c.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[12px] font-black text-slate-900">
                        {c.value_emoji} {c.value_label}
                      </span>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtFull(c.created_at)}</span>
                    </div>
                    <p className="text-[12px] text-slate-500">from {c.from_name}</p>
                    {c.note && <p className="text-[13px] text-slate-700 leading-snug mt-0.5">“{c.note}”</p>}
                  </div>
                ))
              )}
              {hasMore && (
                <button onClick={loadMore} disabled={loadingMore} className="w-full text-[12px] font-black text-blue-600 hover:text-blue-700 py-2 rounded-lg hover:bg-blue-50" data-testid="recognition-person-loadmore">
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
            <div className="px-4 py-3 border-t border-slate-200">
              <button onClick={onClose} className="w-full rounded-full py-2.5 font-black text-slate-600 bg-slate-100 hover:bg-slate-200" data-testid="recognition-person-close">Close</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─── Animated keepsake card: envelope → confetti → flip to read ──────────
const RCV_OPENED_KEY = "wow_rec_opened";
function rcvWasOpened(id) {
  try { return (JSON.parse(localStorage.getItem(RCV_OPENED_KEY) || "[]") || []).includes(id); }
  catch (_) { return false; }
}
function rcvMarkOpened(id) {
  try {
    const arr = JSON.parse(localStorage.getItem(RCV_OPENED_KEY) || "[]") || [];
    if (!arr.includes(id)) { arr.push(id); localStorage.setItem(RCV_OPENED_KEY, JSON.stringify(arr.slice(-800))); }
  } catch (_) { /* ignore */ }
}
function rcvConfetti() {
  try {
    const base = { startVelocity: 42, spread: 360, ticks: 80, zIndex: 2147483647, disableForReducedMotion: true };
    const colors = ["#2563EB", "#EC4899", "#16A34A", "#7C3AED", "#F59E0B", "#ffffff"];
    confetti({ ...base, particleCount: 90, origin: { x: 0.5, y: 0.4 }, colors });
    setTimeout(() => confetti({ ...base, particleCount: 50, angle: 60, origin: { x: 0, y: 0.6 }, colors }), 150);
    setTimeout(() => confetti({ ...base, particleCount: 50, angle: 120, origin: { x: 1, y: 0.6 }, colors }), 150);
  } catch (_) { /* ignore */ }
}

function RecognitionCardViewer({ card, onClose, own = true }) {
  const color = card.value_color || "#2563EB";
  // On the Board (own=false) we skip the envelope reveal AND confetti — that
  // celebration is reserved for YOUR personal cards. Board just shows the card.
  const firstTime = useRef(own ? !rcvWasOpened(card.id) : false);
  const [opening, setOpening] = useState(firstTime.current);   // envelope visible
  const [flapOpen, setFlapOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const who = own ? "you" : (card.to_name || "them");
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch (_) { return ""; } };

  useEffect(() => {
    if (!firstTime.current) { if (own) rcvConfetti(); return undefined; }
    const t1 = setTimeout(() => setFlapOpen(true), 400);
    const t2 = setTimeout(() => { setOpening(false); if (own) rcvMarkOpened(card.id); rcvConfetti(); }, 1020);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [card.id, own]);

  // Keep long notes inside the fixed card: shrink the font and grow card height
  // with the message length (scroll as a final fallback for extreme notes).
  const noteLen = (card.note || "").length;
  const noteSize = noteLen <= 90 ? "text-xl" : noteLen <= 180 ? "text-lg" : noteLen <= 300 ? "text-base" : "text-sm";
  const cardH = noteLen <= 120 ? 400 : noteLen <= 220 ? 460 : noteLen <= 340 ? 520 : 560;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center p-5" style={{ background: "rgba(2,6,23,0.8)", backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="recognition-card-viewer">
      <style>{`
        @keyframes rcvEnvPop{0%{transform:scale(.7);opacity:0}100%{transform:scale(1);opacity:1}}
        @keyframes rcvCardIn{0%{opacity:0;transform:scale(.6) translateY(24px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes rcvFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
        .rcv-card{perspective:1400px}
        .rcv-inner{position:relative;width:100%;height:100%;transform-style:preserve-3d;transition:transform .7s cubic-bezier(.2,.75,.2,1)}
        .rcv-inner.rcv-flipped{transform:rotateY(180deg)}
        .rcv-face{position:absolute;inset:0;backface-visibility:hidden;-webkit-backface-visibility:hidden;border-radius:24px;overflow:hidden;box-shadow:0 30px 60px rgba(0,0,0,.5);display:flex;flex-direction:column}
        .rcv-back{transform:rotateY(180deg)}
        .rcv-flap{position:absolute;top:0;left:50%;transform:translateX(-50%);width:0;height:0;border-left:150px solid transparent;border-right:150px solid transparent;border-top:104px solid rgba(255,255,255,.16);transform-origin:top center;transition:transform .55s ease;z-index:6}
        .rcv-flap.open{transform:translateX(-50%) rotateX(-172deg);z-index:1}
      `}</style>

      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-2xl font-black leading-none active:scale-95 transition"
        style={{ backdropFilter: "blur(4px)" }}
        aria-label="Close"
        data-testid="recognition-card-viewer-x"
      >×</button>

      {opening ? (
        <div className="relative" style={{ width: 300, height: 200, animation: "rcvEnvPop .45s cubic-bezier(.17,.67,.3,1.33) both" }} onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 rounded-2xl" style={{ background: `linear-gradient(160deg, ${color}, #7c3aed)`, boxShadow: "0 24px 50px rgba(0,0,0,.45)" }} />
          <div className="absolute bottom-0 left-0 right-0 rounded-b-2xl" style={{ height: "56%", background: "rgba(0,0,0,.18)", clipPath: "polygon(0 28%,50% 100%,100% 28%,100% 100%,0 100%)" }} />
          <div className={`rcv-flap ${flapOpen ? "open" : ""}`} />
          <div className="absolute left-1/2 -translate-x-1/2 rounded-full bg-white flex items-center justify-center shadow-lg transition-opacity" style={{ top: 74, width: 54, height: 54, fontSize: 26, opacity: flapOpen ? 0 : 1, zIndex: 7 }}>🖐️</div>
          <div className="absolute bottom-4 left-0 right-0 text-center text-white text-[11px] font-black uppercase tracking-widest" style={{ zIndex: 7 }}>{own ? "A High Five for you" : `A High Five for ${card.to_name}`}</div>
        </div>
      ) : (
        <div className="rcv-card w-[300px]" style={{ height: cardH, animation: "rcvCardIn .6s cubic-bezier(.17,.67,.3,1.33) both" }} onClick={(e) => { e.stopPropagation(); setFlipped((f) => !f); }}>
          <div className={`rcv-inner ${flipped ? "rcv-flipped" : ""}`}>
            {/* front */}
            <div className="rcv-face text-white items-center justify-center text-center p-7" style={{ background: `linear-gradient(160deg, ${color} 0%, #7c3aed 70%, #db2777 130%)` }}>
              <div className="text-[70px]" style={{ animation: "rcvFloat 2.6s ease-in-out infinite" }}>{card.value_emoji}</div>
              <h1 className="text-3xl font-black leading-none mt-3" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{own ? <>You got a<br />High Five!</> : <>{card.to_name} got a<br />High Five!</>}</h1>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-black text-sm" style={{ color }}>{card.value_emoji} {card.value_label}</div>
              <p className="mt-4 text-sm font-black text-white/90">from {card.from_name}</p>
              <p className="text-xs text-white/65">{fmt(card.created_at)}</p>
              <p className="absolute bottom-4 left-0 right-0 text-center text-[11px] font-black uppercase tracking-widest text-white/70">Tap to read the note →</p>
            </div>
            {/* back */}
            <div className="rcv-face rcv-back bg-white text-slate-900 text-center px-6 pt-8 pb-11">
              <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: `linear-gradient(90deg, ${color}, #db2777)` }} />
              <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center">
                {card.note
                  ? <p className={`${noteSize} leading-relaxed font-bold text-slate-800`}>“{card.note}”</p>
                  : <p className="text-lg font-bold text-slate-500">Recognized {who} for {card.value_label} {card.value_emoji}</p>}
              </div>
              <div className="mt-3 flex-shrink-0 text-[13px] font-black" style={{ color }}>{card.value_label}<span className="block text-slate-400 font-bold mt-0.5">from {card.from_name} · {fmt(card.created_at)}</span></div>
              <p className="absolute bottom-4 left-0 right-0 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">↻ Tap to flip back</p>
            </div>
          </div>
        </div>
      )}

      {!opening && (
        <button onClick={onClose} className="mt-6 rounded-full bg-white/90 text-slate-900 font-black px-6 py-2.5 active:scale-95 transition-transform" data-testid="recognition-card-viewer-close">Close</button>
      )}
    </div>
  );
}
